import { Controller } from "@hotwired/stimulus"

const SVG_CHECK = '<svg class="w-4 h-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" d="m4.5 12.75 6 6 9-13.5"/></svg>'
const SVG_INFO_CIRCLE = '<svg class="w-4 h-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" d="m11.25 11.25.041-.02a.75.75 0 0 1 1.063.852l-.708 2.836a.75.75 0 0 0 1.063.853l.041-.021M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Zm-9-3.75h.008v.008H12V8.25Z"/></svg>'

// Old-style change orders (Phelipe 2026-09-29). Projects made before July 26
// 2022 keep their COs as Schedule of Values rows - no PCOs to edit - so their
// cards on the Change Orders page open in place to those lines, and can still
// be signed, approved or unsigned from there (the same sign_co / unsign_co
// the new COs use, same who-can-sign rules). This controller only mounts on
// those projects, next to the change-order controller, which stays untouched.
export default class extends Controller {
  static targets = ["signModal", "unsignModal"]

  static values = {
    projectId: Number,
    isContractor: Boolean,
    isConsultant: Boolean,
    // The owner, or a stakeholder granted CO approval (PM Permissions).
    ownerApprover: Boolean,
    signedLabel: String,
    draftLabel: String
  }

  connect() {
    this._csrfToken = document.querySelector('meta[name="csrf-token"]')?.content
    this._sequence = null
    this.element.querySelectorAll("[data-legacy-actions]").forEach(actions => this._applyRules(actions))
  }

  toggle(event) {
    const card = event.currentTarget
    const lines = card.querySelector("[data-legacy-lines]")
    if (!lines) return
    const open = lines.classList.toggle("hidden") === false
    card.querySelector("[data-legacy-chevron]")?.classList.toggle("rotate-180", open)
  }

  stopPropagation(event) {
    event.stopPropagation()
  }

  // -- Sign / approve (both are sign_co - the signer's role decides) --

  sign(event) {
    this._sequence = event.currentTarget.closest("[data-legacy-actions]").dataset.sequence
    this.signModalTarget.classList.remove("hidden")
  }

  hideSignModal() {
    this.signModalTarget.classList.add("hidden")
  }

  async confirmSign(event) {
    await this._post("sign_co", event, this.signModalTarget, "Signed")
  }

  // -- Remove my signature --

  unsign(event) {
    this._sequence = event.currentTarget.closest("[data-legacy-actions]").dataset.sequence
    this.unsignModalTarget.classList.remove("hidden")
  }

  hideUnsignModal() {
    this.unsignModalTarget.classList.add("hidden")
  }

  async confirmUnsign(event) {
    await this._post("unsign_co", event, this.unsignModalTarget, "Signature removed")
  }

  async _post(action, event, modal, message) {
    // One request per gesture - double-clicks and held Enter repeat it.
    if (this._inFlight || !this._sequence) return
    this._inFlight = true
    const button = event?.currentTarget
    if (button) button.disabled = true
    try {
      const body = new FormData()
      body.append("sequence", this._sequence)
      const resp = await fetch(`/projects/${this.projectIdValue}/finances/${action}`, {
        method: "POST",
        headers: { "X-CSRF-Token": this._csrfToken, Accept: "application/json" },
        body
      })
      const data = resp.ok ? await resp.json().catch(() => null) : null
      modal.classList.add("hidden")
      if (!data) {
        this._showToast("Something went wrong. Please try again.", "alert")
        return
      }
      this._refreshCard(data)
      this._showToast(message, "success")
    } catch (e) {
      console.error("Old CO request failed:", e)
      this._showToast("Something went wrong. Please try again.", "alert")
    } finally {
      this._inFlight = false
      if (button) button.disabled = false
    }
  }

  // The card's badge, its buttons, and a PDF link that renders fresh (the
  // stored copy is being regenerated with the new signature).
  _refreshCard(data) {
    const card = this.element.querySelector(`.co-card[data-sequence="${data.sequence}"]`)
    if (!card) return
    const actions = card.querySelector("[data-legacy-actions]")
    if (!actions) return

    Object.assign(actions.dataset, {
      signedCount: String(data.signed_count || 0),
      requiredSigs: String(data.required_sigs || actions.dataset.requiredSigs),
      contractorSigned: String(!!data.contractor_signed),
      consultantSigned: String(!!data.consultant_signed),
      myRoleSigned: String(!!data.my_role_signed)
    })
    card.dataset.signedCount = actions.dataset.signedCount

    const badge = card.querySelector("[data-badge-area]")
    if (badge) badge.innerHTML = this._badgeHTML(actions.dataset, data.signature_tooltip || "")

    const pdfLink = card.querySelector("[data-export-area] a[target='_blank']")
    if (pdfLink) pdfLink.href = `/projects/${this.projectIdValue}/finances/co_export_with_pdfs/${data.sequence}.pdf`

    this._applyRules(actions)
  }

  // Who may do what - the new COs' rules (change-order controller's modal):
  // the contractor or consultant signs while their own signature is missing,
  // the owner approves once the contractor (and consultant, when required)
  // signed, and you can take your signature back until everyone has signed.
  _applyRules(actions) {
    const d = actions.dataset
    const signed = parseInt(d.signedCount) || 0
    const required = parseInt(d.requiredSigs) || 2
    const isPast = d.isPast === "true"
    const contractorSigned = d.contractorSigned === "true"
    const consultantSigned = d.consultantSigned === "true"
    const mySigned = d.myRoleSigned === "true"

    const canSign = !isPast && ((this.isContractorValue && !contractorSigned) || (this.isConsultantValue && !consultantSigned))
    const canApprove = !isPast && !canSign && this.ownerApproverValue && contractorSigned && (consultantSigned || required <= 2) && !mySigned
    const canUnsign = !isPast && mySigned && signed < required

    const show = (selector, on) => { const el = actions.querySelector(selector); if (el) el.hidden = !on }
    show("[data-legacy-sign]", canSign)
    show("[data-legacy-approve]", canApprove)
    show("[data-legacy-unsign]", canUnsign)
    actions.hidden = !(canSign || canApprove || canUnsign)
  }

  _badgeHTML(d, tooltip) {
    const signed = parseInt(d.signedCount) || 0
    const required = parseInt(d.requiredSigs) || 2
    if (d.isPast === "true") return this.element.querySelector(`.co-card[data-sequence="${d.sequence}"] [data-badge-area]`)?.innerHTML || ""
    if (signed === 0) {
      return `<span class="inline-flex items-center gap-1 bg-gray-200 text-gray-600 text-[12px] font-medium rounded-lg px-2.5 py-[2px]">${this._esc(this.draftLabelValue)}</span>`
    }
    const colors = signed < required ? "bg-amber-100 text-amber-600" : "bg-green-100 text-green-600"
    const check = signed >= required && d.myRoleSigned === "true" ? SVG_CHECK.replace("w-4 h-4", "w-3.5 h-3.5") : ""
    const tip = tooltip
      ? `<span class="shrink-0 cursor-help flex items-center" data-tip="${this._esc(tooltip)}" data-tip-align="left" data-tip-pos="below">${SVG_INFO_CIRCLE.replace("w-4 h-4", "w-3.5 h-3.5")}</span>`
      : ""
    return `<span class="inline-flex items-center gap-1 ${colors} text-[12px] font-medium rounded-lg px-2.5 py-[2px]">${check} ${signed}/${required} ${this._esc(this.signedLabelValue)} ${tip}</span>`
  }

  _esc(str) {
    return String(str).replace(/&/g, "&amp;").replace(/"/g, "&quot;").replace(/</g, "&lt;").replace(/>/g, "&gt;")
  }

  _showToast(message, type = "success") {
    const cssType = (type === "error" || type === "alert") ? "alert" : "notice"
    const icon = cssType === "notice"
      ? '<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-5 h-5"><path stroke-linecap="round" stroke-linejoin="round" d="M9 12.75 11.25 15 15 9.75M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z"/></svg>'
      : '<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-5 h-5"><path stroke-linecap="round" stroke-linejoin="round" d="M12 9v3.75m9-.75a9 9 0 1 1-18 0 9 9 0 0 1 18 0Zm-9 3.75h.008v.008H12v-.008Z"/></svg>'
    const el = document.createElement("div")
    el.className = `flash-toast-${cssType}`
    el.style.zIndex = "9999"
    el.setAttribute("data-controller", "flash")
    el.setAttribute("data-flash-dismiss-after-value", "5000")
    el.setAttribute("role", "alert")
    const iconDiv = document.createElement("div")
    iconDiv.className = "shrink-0"
    iconDiv.innerHTML = icon
    const msgP = document.createElement("p")
    msgP.className = "flex-1 text-sm"
    msgP.textContent = message
    el.appendChild(iconDiv)
    el.appendChild(msgP)
    document.body.prepend(el)
  }
}
